// KeyValue — a muted key / value list: identity blocks on the party page, the
// deal panel's rows, the cheque summary.
//
// Item 70 / §5.17 / §5.19: there were two shapes (Section's right-aligned
// Zoho row on the phone, the console kv-grid on desktop) and three local
// `Row` copies. This is the one shape at every width — a fixed label gutter,
// muted micro label, value left beside it, no hairlines.
//
// THE RULE IT ENFORCES (§6 "Internal identifiers", §5.17): the label is a
// plain word the operator uses ("Name", "Type", "Booked by"), never a column
// name; an absent value is an em dash, never a blank cell and never "null".
//
// Usage:
//   <KeyValue rows={[
//     { label: 'Name', value: name },
//     { label: 'Type', value: 'Merchant', hint: 'create as' },
//     { label: 'Created', value: fmtDay(created) },
//   ]} />
//
//   <KeyValue>                                   // ad-hoc rows
//     <KeyValueRow label="Rate" value={fmtRate(rate)} />
//   </KeyValue>

import type { ReactNode } from 'react';

export interface KeyValueItem {
  label: string;
  value: ReactNode;
  /** One short muted line under the label, when the label needs a qualifier. */
  hint?: string;
}

function absent(v: ReactNode): boolean {
  return v === '' || v === null || v === undefined || v === false;
}

export function KeyValueRow({ label, value, hint }: KeyValueItem) {
  return (
    <>
      <dt className="text-label uppercase tracking-[0.05em] text-tg-hint pt-px">
        {label}
        {hint && <span className="block normal-case tracking-normal text-meta opacity-70">{hint}</span>}
      </dt>
      <dd className="text-body min-w-0 break-words m-0">
        {absent(value) ? <span className="text-tg-hint">—</span> : value}
      </dd>
    </>
  );
}

export function KeyValue({ rows, children, className = '' }: {
  rows?: KeyValueItem[];
  children?: ReactNode;
  className?: string;
}) {
  return (
    <dl className={`grid grid-cols-[110px_1fr] items-baseline gap-x-3.5 gap-y-1.5 m-0 ${className}`}>
      {rows?.map((r) => <KeyValueRow key={r.label} {...r} />)}
      {children}
    </dl>
  );
}
